import type {
  CommandResult,
  WorkflowAdvanceInput,
  WorkflowAdvanceOutput,
  WorkflowStartInput,
  WorkflowStartOutput,
} from '@travel-planner/shared/schemas';
import type { AgentPanelAdapter } from './types';
import { useCallback } from 'react';
import { useAgentPanelStore } from './use-agent-panel-store';

export type WorkflowStartMutate = (
  input: WorkflowStartInput
) => Promise<WorkflowStartOutput>;

export type WorkflowAdvanceMutate = (
  input: WorkflowAdvanceInput
) => Promise<WorkflowAdvanceOutput>;

export type UseAgentWorkflowArgs = {
  adapter: AgentPanelAdapter | null | undefined;
  startMutate: WorkflowStartMutate;
  advanceMutate: WorkflowAdvanceMutate;
};

export type UseAgentWorkflowReturn = {
  // Direct entry (sandbox button or programmatic start). Bypasses the
  // planner — the LLM-routed entry path goes through `useAgentCommand`'s
  // step which forwards the planner's extracted `seed` here so the first
  // step doesn't re-ask for things the consultant already said.
  startWorkflow: (
    workflowName: string,
    seed?: Record<string, unknown>
  ) => Promise<void>;
  // Skip the active step (only valid when the active step is `skippable`).
  skipStep: () => Promise<void>;
  // Auto-advance after a step finishes. The command hook calls this when
  // `applyServerStep` sees `phase: 'done'` and a workflow is active.
  advanceFromStepResult: (result: CommandResult) => Promise<void>;
  // Re-run the active step (only valid for `loopable` steps). Selections
  // from prior visits stay intact in `workflowSession.visits`.
  restartStep: () => Promise<void>;
  // Cancel the workflow + the active command session.
  cancel: () => void;
};

const buildStartInput = (
  adapter: AgentPanelAdapter,
  workflowName: string,
  seed?: Record<string, unknown>
): WorkflowStartInput =>
  ({
    contextType: adapter.contextType,
    ...adapter.context,
    workflowName,
    ...(seed && Object.keys(seed).length > 0 ? { seed } : {}),
  }) as WorkflowStartInput;

const buildAdvanceInput = (
  adapter: AgentPanelAdapter,
  workflowName: string,
  activeStepIndex: number,
  completedResults: ReadonlyArray<CommandResult>,
  skippedStepIndices: ReadonlyArray<number>,
  action: WorkflowAdvanceInput['action']
): WorkflowAdvanceInput =>
  ({
    contextType: adapter.contextType,
    ...adapter.context,
    workflowName,
    activeStepIndex,
    completedResults,
    skippedStepIndices,
    action,
  }) as WorkflowAdvanceInput;

export const useAgentWorkflow = ({
  adapter,
  startMutate,
  advanceMutate,
}: UseAgentWorkflowArgs): UseAgentWorkflowReturn => {
  const applyWorkflowStart = useAgentPanelStore((s) => s.applyWorkflowStart);
  const applyWorkflowAdvance = useAgentPanelStore(
    (s) => s.applyWorkflowAdvance
  );
  const appendWorkflowStepResult = useAgentPanelStore(
    (s) => s.appendWorkflowStepResult
  );
  const recordWorkflowSkip = useAgentPanelStore((s) => s.recordWorkflowSkip);
  const cancelWorkflow = useAgentPanelStore((s) => s.cancelWorkflow);
  const setCommandError = useAgentPanelStore((s) => s.setCommandError);

  const startWorkflow = useCallback(
    async (workflowName: string, seed?: Record<string, unknown>) => {
      if (!adapter) return;
      const name = workflowName.trim();
      if (!name) return;
      try {
        const out = await startMutate(buildStartInput(adapter, name, seed));
        applyWorkflowStart(out);
      } catch (err) {
        setCommandError(
          err instanceof Error ? err.message : 'Workflow start failed'
        );
      }
    },
    [adapter, startMutate, applyWorkflowStart, setCommandError]
  );

  const advanceFromStepResult = useCallback(
    async (result: CommandResult) => {
      if (!adapter) return;
      const ws = useAgentPanelStore.getState().workflowSession;
      if (!ws || ws.phase !== 'step-active') return;
      // Append the result to the local accumulator BEFORE the API call so
      // the next call's `completedResults` is up-to-date.
      appendWorkflowStepResult(result);
      const nextCompleted = [...ws.completedResults, result];
      try {
        const out = await advanceMutate(
          buildAdvanceInput(
            adapter,
            ws.workflow,
            ws.activeStepIndex,
            nextCompleted,
            ws.skippedStepIndices,
            { kind: 'step-result', result }
          )
        );
        applyWorkflowAdvance(out);
      } catch (err) {
        setCommandError(
          err instanceof Error ? err.message : 'Workflow advance failed'
        );
      }
    },
    [
      adapter,
      advanceMutate,
      appendWorkflowStepResult,
      applyWorkflowAdvance,
      setCommandError,
    ]
  );

  const skipStep = useCallback(async () => {
    if (!adapter) return;
    const ws = useAgentPanelStore.getState().workflowSession;
    if (!ws || ws.phase !== 'step-active') return;
    const activeStep = ws.steps[ws.activeStepIndex];
    if (!activeStep?.skippable) {
      setCommandError('This step cannot be skipped.');
      return;
    }
    const skipIndex = ws.activeStepIndex;
    recordWorkflowSkip(skipIndex);
    const nextSkipped = [...ws.skippedStepIndices, skipIndex];
    try {
      const out = await advanceMutate(
        buildAdvanceInput(
          adapter,
          ws.workflow,
          ws.activeStepIndex,
          ws.completedResults,
          nextSkipped,
          { kind: 'skip' }
        )
      );
      applyWorkflowAdvance(out);
    } catch (err) {
      setCommandError(
        err instanceof Error ? err.message : 'Workflow skip failed'
      );
    }
  }, [
    adapter,
    advanceMutate,
    recordWorkflowSkip,
    applyWorkflowAdvance,
    setCommandError,
  ]);

  const restartCurrentStepInStore = useAgentPanelStore(
    (s) => s.restartCurrentWorkflowStep
  );

  const restartStep = useCallback(async () => {
    if (!adapter) return;
    const ws = useAgentPanelStore.getState().workflowSession;
    if (!ws || ws.phase !== 'step-active') return;
    const activeStep = ws.steps[ws.activeStepIndex];
    if (!activeStep?.loopable) {
      setCommandError('This step cannot be re-run.');
      return;
    }
    try {
      const out = await advanceMutate(
        buildAdvanceInput(
          adapter,
          ws.workflow,
          ws.activeStepIndex,
          ws.completedResults,
          ws.skippedStepIndices,
          { kind: 'restart-step' }
        )
      );
      // The runner returns a workflow-step open snapshot for the SAME
      // step index; we only need to refresh the commandSession portion
      // (the workflowSession + visits stay intact so prior picks survive).
      if (out.phase === 'workflow-step') {
        restartCurrentStepInStore(
          out.seedCollected,
          out.nextQuestion,
          out.missing
        );
      }
    } catch (err) {
      setCommandError(
        err instanceof Error ? err.message : 'Workflow restart failed'
      );
    }
  }, [adapter, advanceMutate, restartCurrentStepInStore, setCommandError]);

  const cancel = useCallback(() => {
    cancelWorkflow();
  }, [cancelWorkflow]);

  return {
    startWorkflow,
    skipStep,
    advanceFromStepResult,
    restartStep,
    cancel,
  };
};
